import api from "./apiClient";

/* ===================================================== */
/* 🔧 CONSTANTS                                          */
/* ===================================================== */

const NOTIFICATION_TYPES = new Set([
  "task",
  "milestone",
  "investor",
  "system",
]);

const MAX_TITLE_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 2000;
const MAX_REDIRECT_LENGTH = 500;

/* ===================================================== */
/* 🔧 ERROR NORMALIZER                                   */
/* ===================================================== */

function normalizeError(err, fallbackMessage) {
  return new Error(
    err?.response?.data?.message ||
      err?.message ||
      fallbackMessage
  );
}

/* ===================================================== */
/* 🔐 VALIDATORS                                         */
/* ===================================================== */

function normalizeRequiredString(value, fieldName, maxLength) {
  if (typeof value !== "string") {
    throw new Error(`${fieldName} is required`);
  }

  const normalized = value.trim();

  if (!normalized) {
    throw new Error(`${fieldName} is required`);
  }

  if (normalized.length > maxLength) {
    throw new Error(
      `${fieldName} must be ${maxLength} characters or fewer`
    );
  }

  return normalized;
}

function normalizeOptionalString(value, fieldName, maxLength) {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value !== "string") {
    throw new Error(`${fieldName} must be a string`);
  }

  const normalized = value.trim();

  if (!normalized) {
    return undefined;
  }

  if (normalized.length > maxLength) {
    throw new Error(
      `${fieldName} must be ${maxLength} characters or fewer`
    );
  }

  return normalized;
}

function validateUserId(userId) {
  if (
    typeof userId !== "string" ||
    !userId.trim()
  ) {
    throw new Error("Target userId is required");
  }

  return userId.trim();
}

function validateNotificationType(type) {
  const normalized = String(type || "system")
    .trim()
    .toLowerCase();

  if (!NOTIFICATION_TYPES.has(normalized)) {
    throw new Error(
      "Invalid notification type. Allowed types: task, milestone, investor, system"
    );
  }

  return normalized;
}

function validateNotificationId(notificationId) {
  if (
    typeof notificationId !== "string" ||
    !notificationId.trim()
  ) {
    throw new Error("Notification ID is required");
  }

  const id = notificationId.trim();

  /*
   * MongoDB ObjectId validation.
   * Keeps malformed IDs from generating unnecessary API requests.
   */
  if (!/^[a-fA-F0-9]{24}$/.test(id)) {
    throw new Error("Invalid notification ID");
  }

  return id;
}

/* ===================================================== */
/* 🔔 LIST NOTIFICATIONS                                 */
/* ===================================================== */

export const listNotifications = async () => {
  try {
    /*
     * apiClient already attaches:
     * - Authorization
     * - X-Company-Id
     *
     * Do not add those headers here.
     */
    const { data } = await api.get("/notifications");

    const list = Array.isArray(data)
      ? data
      : Array.isArray(data?.notifications)
        ? data.notifications
        : [];

    return list;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to fetch notifications"
    );
  }
};

/* ===================================================== */
/* 📤 SEND NOTIFICATION                                  */
/* ===================================================== */

export const sendNotification = async (payload = {}) => {
  const userId = validateUserId(payload.userId);

  const title = normalizeRequiredString(
    payload.title,
    "Notification title",
    MAX_TITLE_LENGTH
  );

  const message = normalizeRequiredString(
    payload.message,
    "Notification message",
    MAX_MESSAGE_LENGTH
  );

  const type = validateNotificationType(payload.type);

  const redirectTo = normalizeOptionalString(
    payload.redirectTo,
    "Notification redirectTo",
    MAX_REDIRECT_LENGTH
  );

  const requestPayload = {
    userId,
    title,
    message,
    type,
  };

  if (redirectTo !== undefined) {
    requestPayload.redirectTo = redirectTo;
  }

  try {
    const { data } = await api.post(
      "/notifications/send",
      requestPayload
    );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to send notification"
    );
  }
};

/* ===================================================== */
/* ✅ MARK SINGLE NOTIFICATION AS READ                   */
/* ===================================================== */

export const markNotificationRead = async (
  notificationId
) => {
  const id = validateNotificationId(notificationId);

  try {
    const { data } = await api.patch(
      `/notifications/${encodeURIComponent(id)}/read`
    );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to mark notification as read"
    );
  }
};

/* ===================================================== */
/* ✅ MARK ALL NOTIFICATIONS AS READ                      */
/* ===================================================== */

export const markAllNotificationsRead = async () => {
  try {
    const { data } = await api.patch(
      "/notifications/read-all"
    );

    return data;
  } catch (err) {
    throw normalizeError(
      err,
      "Failed to mark all notifications as read"
    );
  }
};

/* ===================================================== */
/* 📦 EXPORT                                             */
/* ===================================================== */

const notificationService = {
  listNotifications,
  sendNotification,
  markNotificationRead,
  markAllNotificationsRead,
};

export default notificationService;